import type { Editor } from '@tiptap/react'
import type { Node as PMNode } from '@tiptap/pm/model'
import { HIGHLIGHT_COLOURS, isHighlightColour } from './highlight-mark'
import { TEXT_COLOURS, TEXT_COLOUR_TOKEN, isTextColourName } from './text-colour-mark'

/**
 * What the prose format toolbar feeds the common colour panel (components/colour/ColourPicker.tsx,
 * AB#287). A prose part has no slides theme, so its palettes are fixed: the five highlight colours
 * (an existing `==x=={green}` shows its square as selected) and the text colours of the design
 * tokens. Anything else is a custom `#RRGGBB`.
 */

/** The palette squares of each mark, in order. */
export const PROSE_PALETTE = { highlight: HIGHLIGHT_COLOURS, textColour: TEXT_COLOURS } as const

export type ColourMark = keyof typeof PROSE_PALETTE

/** How many custom colours the panel offers again ("recent"). Same cap as the slides panel. */
export const RECENT_PROSE_COLOURS = 8

/** A mark's value → the CSS colour the editor draws it with (a token for a named one). */
export function proseSwatch(mark: ColourMark, value: string): string {
  if (value.startsWith('#')) return value
  if (mark === 'highlight') return isHighlightColour(value) ? `var(--hl-${value})` : value
  return isTextColourName(value) ? TEXT_COLOUR_TOKEN[value] : value
}

/** The selection's value of a colour mark: one value, none (null), or several ('mixed'). */
export function colourAt(e: Editor, mark: ColourMark): string | null | 'mixed' {
  const { from, to, empty } = e.state.selection
  // A caret: the marks it would type with (stored marks included), as isActive reads them.
  if (empty) return e.isActive(mark) ? String(e.getAttributes(mark).color ?? '') || null : null
  const seen = new Set<string | null>()
  e.state.doc.nodesBetween(from, to, (n) => {
    if (!n.isText) return
    const m = n.marks.find((x) => x.type.name === mark)
    seen.add(m ? String(m.attrs.color) : null)
  })
  if (seen.size > 1) return 'mixed'
  return seen.size === 1 ? [...seen][0]! : null
}

/** The custom (`#RRGGBB`) colours the part already uses, text colour or highlight, in document order. */
export function customColoursIn(doc: PMNode, max = RECENT_PROSE_COLOURS): string[] {
  const out: string[] = []
  doc.descendants((n) => {
    if (out.length >= max) return false
    if (!n.isText) return true
    for (const m of n.marks) {
      const c: unknown = m.attrs.color
      if ((m.type.name === 'textColour' || m.type.name === 'highlight') && typeof c === 'string' && c.startsWith('#') && !out.includes(c)) {
        out.push(c)
      }
    }
    return false
  })
  return out.slice(0, max)
}
